import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fymAPI, useGetReelQuery } from '../../../../../redux/api/fymAPI';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';
import { isReelWorking, ReelStage, reelStageOfStatus } from './reelStages';

/** The poll interval of a working reel (design b1). */
export const REEL_POLL_MS = 4000;

interface UseReelPollProps {
  accountId: number;
  /** Undefined: no reel is open, so nothing is fetched. */
  reelUuid?: string;
}

/**
 * Reads one reel and polls it every 4 s while it works (queued, analysing, planning, rendering).
 * While the realtime socket is connected, the `reel.updated` event reads the reel again and the
 * hook does not poll. The poll stops on ready, approved, rejected and failed. It also keeps the last stage it saw, so
 * a STALLED reel can show where it stopped. When a reel it saw working stops, it refetches the
 * reels list, so the history card of that reel shows the new status.
 */
export function useReelPoll({ accountId, reelUuid }: UseReelPollProps) {
  const dispatch = useDispatch();
  const [lastSeenStage, setLastSeenStage] = useState<ReelStage | null>(null);
  // The uuid of the reel this hook last saw working. Null when it saw none.
  const workingUuid = useRef<string | null>(null);

  // A reel with no answer yet is a new one: keep the poll on until the first answer.
  const [pollingInterval, setPollingInterval] = useState(REEL_POLL_MS);

  // `currentData`, not `data`: `data` still holds the previous reel while a new uuid loads.
  const { currentData, error, isLoading, refetch } = useGetReelQuery(
    { accountId, reelUuid: reelUuid ?? '' },
    { skip: reelUuid === undefined, pollingInterval: useRealtimePollInterval(pollingInterval) }
  );

  const reel = currentData?.reel;
  const status = reel?.status;
  const uuid = reel?.uuid;
  // A failed read with no reel on screen (a 404 deep link, a 5xx) stops the poll: the screen
  // shows ErrorView, and its retry refetches.
  const hasFailed = error !== undefined && currentData === undefined;

  useEffect(() => {
    setLastSeenStage(null);
    setPollingInterval(REEL_POLL_MS);
  }, [reelUuid]);

  useEffect(() => {
    if (hasFailed) {
      setPollingInterval(0);
      return;
    }
    if (status === undefined || uuid === undefined) {
      return;
    }
    const isWorking = isReelWorking(status);
    setPollingInterval(isWorking ? REEL_POLL_MS : 0);
    if (isWorking) {
      workingUuid.current = uuid;
    } else if (workingUuid.current === uuid) {
      workingUuid.current = null;
      // The history strip lists this reel with the status of its last fetch. The tag also
      // matches this reel's own query, which then reads the final state once more.
      dispatch(fymAPI.util.invalidateTags([{ type: 'Reels', id: uuid }]));
    }
    const stage = reelStageOfStatus(status);
    if (stage !== null) {
      setLastSeenStage(stage);
    }
  }, [dispatch, hasFailed, status, uuid]);

  return { reel, error, isLoading, refetch, lastSeenStage };
}
